/**
 * Clear mapping / child values when setting_controls hides a row.
 * Visibility rules live in settingControlVisibility.ts.
 */

import {buildFieldMappingMetaMap, enrichFieldMapping} from './fieldMappingMetaUtils'
import {isMappingHiddenByChannelSettingsRenderRules} from './fieldMappingSaveUtils'
import {
  getSettingControls,
  shouldRenderChildByDropdownControl,
  shouldRenderMappingByDropdownControl,
} from './settingControlVisibility'
export type SettingControlMappingPair = {
  thisSideMappings: any[]
  oppositeSideMappings: any[]
  fieldMappingsMeta?: any[]
}

const hasMappingDatasetValues = (entry: any): boolean => {
  const rows = entry?.fieldMappingDataSet ?? entry?.fieldmappingdata_set ?? []
  if (!Array.isArray(rows) || !rows.length) {
    return false
  }
  return rows.some((row) => {
    const dest = row?.destination_platform_data_id ?? row?.destination_platform_data_str
    const source = row?.source_platform_data_id ?? row?.source_platform_data_str
    return (
      (dest !== null && dest !== undefined && `${dest}`.trim() !== '') ||
      (source !== null && source !== undefined && `${source}`.trim() !== '')
    )
  })
}

export const clearFieldMappingPendingEntry = (entry: any): any => ({
  ...entry,
  fieldMappingDataSet: [],
  fieldmappingdata_set: [],
})

export const pruneFieldMappingsHiddenBySettingControl = (
  fieldMappings: any[],
  pairs: SettingControlMappingPair[],
  savedMappings: any[] = []
): any[] => {
  if (!fieldMappings?.length || !pairs.length) {
    return fieldMappings
  }

  let changed = false
  const next = fieldMappings.map((entry) => {
    const settingMappingId = entry?.setting_mapping_id
    if (settingMappingId === null || settingMappingId === undefined) {
      return entry
    }

    for (const pair of pairs) {
      const metaMap = buildFieldMappingMetaMap(pair.fieldMappingsMeta ?? [])
      const matchesId = (row: any) =>
        row?.setting_mapping_id === settingMappingId || row?.id === settingMappingId

      const catalogOnThis = (pair.thisSideMappings ?? []).find(matchesId)
      const catalogOnOpposite = (pair.oppositeSideMappings ?? []).find(matchesId)
      const catalogRow = catalogOnThis ?? catalogOnOpposite
      if (!catalogRow || !getSettingControls(catalogRow).length) {
        continue
      }

      const enriched = enrichFieldMapping(catalogRow, metaMap)
      const thisSide = catalogOnThis
        ? pair.thisSideMappings ?? []
        : pair.oppositeSideMappings ?? []
      const oppositeSide = catalogOnThis
        ? pair.oppositeSideMappings ?? []
        : pair.thisSideMappings ?? []

      const visible = shouldRenderMappingByDropdownControl(
        enriched,
        thisSide,
        fieldMappings,
        savedMappings,
        oppositeSide
      )
      if (!visible && hasMappingDatasetValues(entry)) {
        changed = true
        return clearFieldMappingPendingEntry(entry)
      }
      return entry
    }

    return entry
  })

  return changed ? next : fieldMappings
}

export type SettingControlChildPair = {
  thisSideChildren: any[]
  oppositeSideChildren: any[]
  settingField: string
  thisSideMappings: any[]
  oppositeSideMappings: any[]
}

const defaultHiddenChildValue = (inputType?: string) => {
  if (inputType === 'MULTISELECT') {
    return ''
  }
  if (inputType === 'BOOLEAN') {
    return false
  }
  return ''
}

export const pruneFormValuesHiddenChildrenBySettingControl = (
  formValues: Record<string, any>,
  pairs: SettingControlChildPair[],
  pendingMappings: any[] = [],
  savedMappings: any[] = []
): {values: Record<string, any>; changed: boolean} => {
  if (!pairs.length) {
    return {values: formValues, changed: false}
  }

  let changed = false
  const next = {...formValues}

  pairs.forEach(
    ({thisSideChildren, oppositeSideChildren, settingField, thisSideMappings, oppositeSideMappings}) => {
    const containerKey = `${settingField}_setting`
    const container = {...(next[containerKey] ?? {})}
    let containerChanged = false

    ;(thisSideChildren ?? []).forEach((child) => {
      if (!child?.name || !getSettingControls(child).length) {
        return
      }
      const visible = shouldRenderChildByDropdownControl(
        child,
        thisSideChildren ?? [],
        next,
        settingField,
        oppositeSideChildren ?? [],
        {
          thisSideMappings: thisSideMappings ?? [],
          oppositeSideMappings: oppositeSideMappings ?? [],
          pendingMappings,
          savedMappings,
        }
      )
      if (visible) {
        return
      }
      const current = container[child.name]
      const cleared = defaultHiddenChildValue(child.input_type)
      if (current !== cleared && current !== undefined && current !== null && `${current}` !== '') {
        container[child.name] = cleared
        containerChanged = true
      }
    })

    if (containerChanged) {
      next[containerKey] = container
      changed = true
    }
  }
  )

  return {values: changed ? next : formValues, changed}
}

export const buildSettingControlMappingPairs = (
  thisSetting: any,
  oppositeSetting: any | null | undefined
): SettingControlMappingPair | null => {
  if (!thisSetting?.mappings_data?.length) {
    return null
  }
  return {
    thisSideMappings: thisSetting.mappings_data,
    oppositeSideMappings: oppositeSetting?.mappings_data ?? [],
    fieldMappingsMeta: thisSetting.field_mappings ?? [],
  }
}

export const buildSettingControlChildPair = (
  thisSetting: any,
  oppositeSetting: any | null | undefined,
  settingField: string
): SettingControlChildPair | null => {
  if (!thisSetting?.children?.length) {
    return null
  }
  return {
    thisSideChildren: thisSetting.children,
    oppositeSideChildren: oppositeSetting?.children ?? [],
    settingField,
    thisSideMappings: thisSetting?.mappings_data ?? [],
    oppositeSideMappings: oppositeSetting?.mappings_data ?? [],
  }
}

export const findGroupSettingForCatalogMapping = (
  settingsCatalog: any[] = [],
  settingMappingId?: number | null
): {groupSetting: any; mapping: any} | null => {
  if (settingMappingId === null || settingMappingId === undefined) {
    return null
  }

  for (const groupSetting of settingsCatalog) {
    const mapping = (groupSetting?.mappings_data || []).find(
      (row: any) =>
        row?.setting_mapping_id === settingMappingId || row?.id === settingMappingId
    )
    if (mapping) {
      return {groupSetting, mapping}
    }
  }

  return null
}

/** Dropdown/OR visibility plus location/shipping hide rules; hidden required mappings must not block save. */
export const isPendingFieldMappingVisibleForSettingControl = (
  settingMappingId: number | null | undefined,
  settingsCatalog: any[],
  fieldMappings: any[],
  savedMappings: any[],
  resolveOppositeGroupSetting: (groupSetting: any) => any | null | undefined
): boolean => {
  const found = findGroupSettingForCatalogMapping(settingsCatalog, settingMappingId)
  if (!found) {
    return true
  }

  const oppositeGroupSetting = resolveOppositeGroupSetting(found.groupSetting)
  const metaMap = buildFieldMappingMetaMap(found.groupSetting?.field_mappings ?? [])
  const enriched = enrichFieldMapping(found.mapping, metaMap)

  if (
    !shouldRenderMappingByDropdownControl(
      enriched,
      found.groupSetting?.mappings_data ?? [],
      fieldMappings,
      savedMappings,
      oppositeGroupSetting?.mappings_data ?? []
    )
  ) {
    return false
  }

  if (
    isMappingHiddenByChannelSettingsRenderRules({
      mapping: enriched,
      owningGroupSetting: found.groupSetting,
      oppositeGroupSetting,
      allPendingMappings: fieldMappings,
      allSavedMappings: savedMappings,
    })
  ) {
    return false
  }

  return true
}
